/**
 * Regelversion 12: Bilanzbericht „Warum hat sich etwas verändert?“ (im Code noch „Quartalsbericht“, qr/qs).
 *
 * In der ersten Legislatur alle sechs Monate (Halbjahresbilanz), danach einmal im Jahr (Jahresbilanz, wie der
 * Jahreswirtschaftsbericht) fasst eine Karte zusammen, was Zustimmung und Umfrage bewegt hat. Die Zustimmung
 * folgt einem Zielwert aus Problemdruck, Haushalt, Wirtschaft, Inflation und den Wirkungen von Gesetzen
 * und Ereignissen (govern.ts, moods). Der Bericht vergleicht diese Bausteine zu Beginn und am Ende des
 * Quartals, Gesetze werden dabei Monat für Monat mitgezählt. Die Umfrage hängt zusätzlich an der Treue
 * der Stammwähler und am Echo in der Presse.
 * Abschaltbar in den Einstellungen. Nur im Einzelspieler. Zieht keinen Zufall.
 */
import { S } from './state';
import { since } from './version';
import { GROUPS, WEIGHT } from '../content/axes';
import { issuesNow } from './axes';
import { debtLimit } from './budget';
import { inflPen } from './economy';
import { approval, poll } from './govern';
import { polarLabel } from './polar';
import { dateLabel, de } from './util';

const A = () => S as any;
export const qrOn = (): boolean => since(12) && !S.mp && A().cfg?.qr !== false;

type Comp = { bonus: number; prob: number; haus: number; wirt: number; infl: number };
export type QSnap = { m: number; appr: number; poll: number; c: Comp; sev: Record<string, number>; gr: number; al: number; basis: number; press: number; saldo: number; law: number; decay: number; laws: string[]; pol?: number; polar0?: number };
export type QPart = { k: string; n: string; v: number; d: string; poll?: boolean };
export type QReport = { k?: 'halb' | 'jahr'; from: number; to: number; appr: [number, number]; poll: [number, number]; parts: QPart[] };

function comps(): Comp {
  const act = issuesNow().filter((i: any) => S.issues[i.id]?.on || S.issues[i.id]?.solved != null);
  const def = Math.min(30, Math.max(0, debtLimit() - S.saldo) * .45);
  const c: Comp = { bonus: 0, prob: 0, haus: -def, wirt: (S.gr - .6) * 5 - (S.al - 6.2) * 4, infl: 0 };
  for (const g in GROUPS) {
    const w = WEIGHT[g] || 0; let n = 0, d = 0;
    act.forEach((i: any) => { const x = i.imp[g] || 0; n += S.issues[i.id].sev * x; d += x; });
    c.bonus += w * (S.bonus[g] || 0); c.prob -= w * (d ? n / d * .3 : 0); c.infl -= w * inflPen(g);
  }
  return c;
}
export function qSnap() {
  if (!qrOn()) return;
  A().qs = { m: S.m, appr: approval(), poll: poll(), c: comps(), sev: Object.fromEntries(Object.keys(S.issues).filter((k) => S.issues[k].on).map((k) => [k, S.issues[k].sev])),
    gr: S.gr, al: S.al, basis: S.basis || 0, press: S.press || 0, saldo: S.saldo, law: 0, decay: 0, laws: [], pol: 0, polar0: A().polar || 0 } as QSnap;
}
/** Wirkung eines Gesetzes in diesem Monat (gewichtet nach Wählergruppen). Aufruf aus tick(). */
export function qLaw(g: Record<string, number>, n: number, t: string) {
  const q: QSnap | undefined = A().qs; if (!q || !qrOn()) return;
  let w = 0; for (const k in g) w += (WEIGHT[k] || 0) * g[k] / n;
  q.law += w; if (Math.abs(w) > .05 && !q.laws.includes(t)) q.laws.push(t);
}
/** Nachlassende Wirkung früherer Erfolge und Ereignisse (bonus *= .97). Aufruf aus tick() vor dem Abklingen. */
export function qDecay() {
  const q: QSnap | undefined = A().qs; if (!q || !qrOn()) return;
  for (const g in S.bonus) q.decay += (WEIGHT[g] || 0) * S.bonus[g] * .03;
}

/** Am Ende des Monats: alle drei Monate ein Bericht. Gibt true zurück, wenn er erscheint. */
export function qTick(): boolean {
  if (!qrOn() || S.phase !== 'govern') return false;
  const q: QSnap | undefined = A().qs; if (!q) { qSnap(); return false; }
  if (S.m - q.m < qPeriod()) return false;
  const c = comps(), parts: QPart[] = [];
  const up = Object.keys(S.issues).filter((k) => S.issues[k].on).map((k) => ({ k, d: S.issues[k].sev - (q.sev[k] ?? S.issues[k].sev) })).sort((a, b) => b.d - a.d);
  const ISSN = (k: string) => issuesNow().find((i: any) => i.id === k)?.n || k;
  const worse = up.filter((x) => x.d > 1.5).slice(0, 2).map((x) => ISSN(x.k)), better = up.filter((x) => x.d < -1.5).reverse().slice(0, 2).map((x) => ISSN(x.k));
  parts.push({ k: 'prob', n: 'Probleme', v: c.prob - q.c.prob, d: worse.length ? `Verschärft: ${worse.join(', ')}.${better.length ? ` Entspannt: ${better.join(', ')}.` : ''}` : better.length ? `Entspannt: ${better.join(', ')}.` : 'Kaum Bewegung bei den Problemen.' });
  parts.push({ k: 'law', n: 'Eure Gesetze', v: q.law, d: q.laws.length ? `Spürbar: ${q.laws.slice(0, 3).map((t) => `„${t}“`).join(', ')}.` : 'Kein Gesetz hat in diesen Monaten spürbar gewirkt.' });
  const pol = q.pol || 0, p0 = q.polar0 ?? 0, p1 = A().polar || 0;
  parts.push({ k: 'polar', n: 'Spaltung im Land', v: pol, d: `Spaltung ${polarLabel(p0)} (${Math.round(p0)}) → ${polarLabel(p1)} (${Math.round(p1)}). ${pol >= 0 ? 'Eure klaren Haltungen begeistern die einen mehr, als sie die anderen verärgern.' : 'Klare Haltungen bei Kulturkampf-Themen spalten: Ein gespaltenes Land kostet Zustimmung, mobilisiert aber eure Basis.'}` });
  const other = c.bonus - q.c.bonus - q.law + q.decay - pol;
  parts.push({ k: 'other', n: 'Ereignisse, Krisen und Reaktionen', v: other, d: 'Zufallsereignisse, Krisenentscheidungen, Verbände und Kommunikation.' });
  parts.push({ k: 'decay', n: 'Frühere Wirkungen verblassen', v: -q.decay, d: 'Gute wie schlechte Nachrichten geraten mit der Zeit in Vergessenheit.' });
  parts.push({ k: 'wirt', n: 'Wirtschaft', v: c.wirt - q.c.wirt, d: `Wachstum ${de(q.gr, 2)} → ${de(S.gr, 2)} %, Arbeitslosigkeit ${de(q.al)} → ${de(S.al)} %.` });
  parts.push({ k: 'haus', n: 'Haushalt', v: c.haus - q.c.haus, d: S.saldo < debtLimit() ? 'Das Defizit liegt über der Schuldenbremse, das kostet Vertrauen.' : 'Der Haushalt hält die Schuldenbremse ein.' });
  parts.push({ k: 'infl', n: 'Inflation', v: c.infl - q.c.infl, d: `Inflation ${de(S.infl)} %.` });
  parts.push({ k: 'basis', n: 'Stammwähler', v: (S.basis || 0) - q.basis, poll: true, d: (S.basis || 0) >= q.basis ? 'Eure Basis steht hinter euch.' : 'Teile eurer Basis sind enttäuscht vom Kurs.' });
  parts.push({ k: 'press', n: 'Presse', v: ((S.press || 0) - q.press) * .3, poll: true, d: S.news[0] ? `Zuletzt: ${S.news[0].o}, „${S.news[0].t}“.` : 'Wenig Schlagzeilen.' });
  const R: QReport = { k: qPeriod() === 6 ? 'halb' : 'jahr', from: q.m, to: S.m, appr: [q.appr, approval()], poll: [q.poll, poll()], parts: parts.filter((p) => Math.abs(p.v) >= .15).sort((a, b) => Math.abs(b.v) - Math.abs(a.v)).slice(0, 6) };
  A().qr = R; qSnap();
  return true;
}
/** Abstand der Berichte in Monaten: erste Legislatur halbjährlich, danach jährlich. */
export const qPeriod = (): number => ((S.leg ?? 0) === 0 ? 6 : 12);
export const qrName = (R: QReport) => (R.k === 'jahr' ? 'Jahresbilanz' : R.k === 'halb' ? 'Halbjahresbilanz' : 'Bilanz');
export function qrOk() { A().qr = null; }
export const qrTitle = (R: QReport) => `${dateLabel(R.from + 1)} bis ${dateLabel(R.to)}`;
